/*
  ATD - Product badges (custom)

  v1.1: aplica insignias a la página de producto individual Y a las
  cards de producto en cualquier página (collection, home, search, etc.).

  La sección emite un <atd-product-badges> con:
   - data-product-id / data-product-tags del producto actual (si page_type=product).
   - <script type="application/json" data-atd-product-badge-block>
     por cada block (insignia configurada).

  Este JS:
   1. Lee todos los blocks en orden (prioridad).
   2. En página de producto: detecta el producto actual y aplica winner
      sobre la galería principal.
   3. En cualquier página: detecta todas las <product-card data-product-id>
      y aplica winner sobre cada card. Usa MutationObserver para detectar
      cards agregadas dinámicamente (paginación AJAX, infinite scroll).

  Limitación: para CARDS, solo funciona match por colección y product_list
  (no por tag), porque las cards no exponen tags al DOM.
*/
(() => {
  if (customElements.get('atd-product-badges')) return;

  const GALLERY_SELECTORS = [
    '.media-gallery__grid',
    '.product-media-container',
    '.product-media-constraint-wrapper',
    'media-gallery',
    '.product-media__drag-zoom-wrapper',
  ];

  // Cards de producto que reciben badge. Cualquier elemento con
  // data-product-id que sea visualmente una card. Incluye:
  //  - <product-card data-product-id> de Horizon estándar.
  //  - <div class="atd-coll-grid1__card" data-product-id> de atd-collection-grid1.
  const CARD_SELECTORS = 'product-card[data-product-id], .atd-coll-grid1__card[data-product-id]';

  // Dentro de cada card, el wrapper donde inyectamos el badge. Preferimos
  // el área de la galería/imagen para que el badge quede sobre el thumbnail.
  const CARD_INNER_SELECTORS = [
    '.product-card__gallery',
    '.product-card__media',
    '.product-card__link',
    '.atd-coll-grid1__card-media',
  ];

  class AtdProductBadges extends HTMLElement {
    connectedCallback() {
      this.blocks = this.#parseBlocks();
      if (this.blocks.length === 0) return;

      // 1. Página de producto individual: usa data attributes.
      const productId = this.dataset.productId;
      if (productId) {
        const productTags = (this.dataset.productTags || '')
          .split('|')
          .map((t) => t.trim().toLowerCase())
          .filter(Boolean);

        const winner = this.#pickWinner(productId, productTags);
        if (winner) {
          this.#findGallery().then((gallery) => {
            if (gallery) this.#injectGalleryBadge(gallery, winner);
          });
        }
      }

      // 2. Cards: aplican siempre, en cualquier página.
      this.#applyToAllCards();

      // 3. Observer global para cards que se agreguen después
      //    (paginación AJAX, filtros, infinite scroll, etc.).
      this._cardObserver = new MutationObserver(() => this.#applyToAllCards());
      this._cardObserver.observe(document.body, {
        childList: true,
        subtree: true,
      });
    }

    disconnectedCallback() {
      if (this._galleryObs) this._galleryObs.disconnect();
      if (this._cardObserver) this._cardObserver.disconnect();
    }

    #parseBlocks() {
      const scripts = this.querySelectorAll('[data-atd-product-badge-block]');
      const blocks = [];
      scripts.forEach((s) => {
        try {
          blocks.push(JSON.parse(s.textContent));
        } catch (e) {
          // ignore mal-formados
        }
      });
      return blocks;
    }

    #pickWinner(productId, productTags) {
      for (const block of this.blocks) {
        if (this.#blockMatches(block, productId, productTags)) return block;
      }
      return null;
    }

    #blockMatches(block, productId, productTags) {
      // Match por product_list directa o colecciones (ambas pre-calculadas en Liquid)
      if ((block.productIds || []).map(String).includes(String(productId))) return true;
      // Match por tag (solo si tenemos tags — página de producto)
      if (productTags && productTags.length > 0) {
        const blockTags = (block.tags || '')
          .split(',')
          .map((t) => t.trim().toLowerCase())
          .filter(Boolean);
        for (const t of blockTags) {
          if (productTags.includes(t)) return true;
        }
      }
      return false;
    }

    // ---------- Galería del producto individual ----------
    async #findGallery(maxWait = 4000) {
      const find = () => {
        for (const sel of GALLERY_SELECTORS) {
          const node = document.querySelector(sel);
          if (node) return node;
        }
        return null;
      };

      const found = find();
      if (found) return found;

      return new Promise((resolve) => {
        const obs = new MutationObserver(() => {
          const node = find();
          if (node) {
            obs.disconnect();
            resolve(node);
          }
        });
        obs.observe(document.body, { childList: true, subtree: true });
        setTimeout(() => {
          obs.disconnect();
          resolve(find());
        }, maxWait);
      });
    }

    #injectGalleryBadge(gallery, block) {
      if (getComputedStyle(gallery).position === 'static') {
        gallery.style.position = 'relative';
      }
      if (gallery.querySelector('[data-atd-custom-badge]')) return;

      const badge = this.#buildBadgeNode(block);
      if (!badge) return;
      gallery.appendChild(badge);

      // Re-inyección si Horizon re-renderiza la galería (ej. cambio variante).
      this._galleryObs = new MutationObserver(() => {
        if (!gallery.querySelector('[data-atd-custom-badge]')) {
          const re = this.#buildBadgeNode(block);
          if (re) gallery.appendChild(re);
        }
      });
      this._galleryObs.observe(gallery, { childList: true, subtree: false });
    }

    // ---------- Cards de producto (collection, home, search, etc.) ----------
    #applyToAllCards() {
      const cards = document.querySelectorAll(CARD_SELECTORS);
      cards.forEach((card) => this.#applyToCard(card));
    }

    #applyToCard(card) {
      // Idempotencia: si ya tiene badge, skip.
      if (card.querySelector('[data-atd-custom-badge]')) return;

      const productId = card.dataset.productId;
      if (!productId) return;

      // Match: solo product_id (no tags en cards).
      const winner = this.#pickWinner(productId, []);
      if (!winner) return;

      // Inyectar dentro del area de imagen para que quede sobre el thumbnail.
      let target = null;
      for (const sel of CARD_INNER_SELECTORS) {
        target = card.querySelector(sel);
        if (target) break;
      }
      if (!target) target = card;

      if (getComputedStyle(target).position === 'static') {
        target.style.position = 'relative';
      }

      const badge = this.#buildBadgeNode(winner);
      if (!badge) return;
      badge.classList.add('atd-custom-badge--card');
      target.appendChild(badge);
    }

    // ---------- Construcción del nodo ----------
    #buildBadgeNode(block) {
      const isImage = block.contentType === 'image' && block.image;
      const wrapper = document.createElement('span');
      wrapper.className =
        'atd-custom-badge ' +
        `atd-custom-badge--${isImage ? 'image' : 'text'} ` +
        `atd-custom-badge--${block.size || 'medium'} ` +
        `atd-custom-badge--${block.position || 'top-left'}`;
      wrapper.dataset.atdCustomBadge = '';

      if (isImage) {
        const img = document.createElement('img');
        img.src = block.image;
        img.alt = block.imageAlt || block.name || '';
        img.loading = 'lazy';
        wrapper.appendChild(img);
      } else {
        wrapper.textContent = block.text || block.name || '';
        if (block.bgColor) wrapper.style.setProperty('--atd-custom-badge-bg', block.bgColor);
        if (block.textColor) wrapper.style.setProperty('--atd-custom-badge-fg', block.textColor);
      }

      return wrapper;
    }
  }

  customElements.define('atd-product-badges', AtdProductBadges);
})();
